import { euclideanDistance } from '@/modules/Editor/algebra'
import { pointInPath } from '@/modules/Editor/point'

export type Pt = { x: number; y: number }
export type Ring = Pt[]
export type Island = { outer: Ring; holes: Ring[] }
export type PolygonShape = { path: Pt[]; additionalPaths: Pt[][] }

export const ringArea = (ring: Ring): number => {
  let sum = 0
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    sum += ring[j].x * ring[i].y - ring[i].x * ring[j].y
  }
  return Math.abs(sum) / 2
}

export const islandArea = (island: Island): number =>
  ringArea(island.outer) - island.holes.reduce((sum, hole) => sum + ringArea(hole), 0)

export const islandContains = (island: Island, p: Pt): boolean =>
  pointInPath(p, island.outer) && !island.holes.some((hole) => pointInPath(p, hole))

export const ringsOf = (shape: { path: Pt[]; additionalPaths?: Pt[][] }): Ring[] => [
  shape.path,
  ...(shape.additionalPaths ?? []),
]

export const shapeFromIslands = (islands: Island[]): PolygonShape => {
  const [first, ...rest] = [...islands].sort((a, b) => islandArea(b) - islandArea(a))
  return {
    path: first.outer,
    additionalPaths: [...first.holes, ...rest.flatMap((island) => [island.outer, ...island.holes])],
  }
}

export const nearestOnSegment = (p: Pt, a: Pt, b: Pt): Pt => {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lengthSquared = dx * dx + dy * dy
  const t = lengthSquared
    ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared))
    : 0
  return { x: a.x + t * dx, y: a.y + t * dy }
}

export const polylineLength = (points: Pt[]): number => {
  let length = 0
  for (let i = 1; i < points.length; i++) {
    length += euclideanDistance(points[i - 1], points[i])
  }
  return length
}

const cross = (a: Pt, b: Pt, c: Pt): number => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x)

const samePoint = (a: Pt, b: Pt): boolean => a.x === b.x && a.y === b.y

export const segmentIntersection = (a: Pt, b: Pt, c: Pt, d: Pt): Pt | null => {
  const rx = b.x - a.x
  const ry = b.y - a.y
  const sx = d.x - c.x
  const sy = d.y - c.y
  const denominator = rx * sy - ry * sx
  if (denominator === 0) {
    return null
  }
  const t = ((c.x - a.x) * sy - (c.y - a.y) * sx) / denominator
  const u = ((c.x - a.x) * ry - (c.y - a.y) * rx) / denominator
  if (t < 0 || t > 1 || u < 0 || u > 1) {
    return null
  }
  return { x: a.x + t * rx, y: a.y + t * ry }
}

// Segments that only share an endpoint do not cross; collinear overlap does.
export const segmentsCross = (a: Pt, b: Pt, c: Pt, d: Pt): boolean => {
  const o1 = Math.sign(cross(a, b, c))
  const o2 = Math.sign(cross(a, b, d))
  const o3 = Math.sign(cross(c, d, a))
  const o4 = Math.sign(cross(c, d, b))
  if (o1 === 0 && o2 === 0) {
    const horizontal = Math.abs(b.x - a.x) >= Math.abs(b.y - a.y)
    const project = (p: Pt): number => (horizontal ? p.x : p.y)
    const [a0, a1] = [project(a), project(b)].sort((p, q) => p - q)
    const [c0, c1] = [project(c), project(d)].sort((p, q) => p - q)
    return Math.min(a1, c1) - Math.max(a0, c0) > 0
  }
  const shared = [a, b].some((p) => samePoint(p, c) || samePoint(p, d))
  if (shared) {
    return false
  }
  if (o1 === 0 || o2 === 0 || o3 === 0 || o4 === 0) {
    return segmentIntersection(a, b, c, d) !== null
  }
  return o1 !== o2 && o3 !== o4
}
